import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Plus, X, User, Shield, Search, Trash2 } from 'lucide-react';
import { Input } from "../ui/input";
import AccountProject from './AccountProject'; // Import the new component
import { toast } from "sonner";

const ProjectDetails = ({ axiosInstance, currentUser }) => {
  const [projectDetails, setProjectDetails] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // View State
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'account-project'
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Delete State
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  // Fetch Projects on Mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/ccp/project-details');
      setProjectDetails(response.data);
    } catch (error) {
      console.error('Error fetching project details:', error);
      toast.error('Failed to load project details');
    }
  };

  const fetchAdmins = async () => {
    if (availableAdmins.length > 0) return; // Don't refetch if we have data
    
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/users/all');
      const all = Array.isArray(response?.data) ? response.data : [];
      setAvailableAdmins(all.filter((u) => u.role === 'admin' || !!u.is_super_admin));
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to load admin list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setIsModalOpen(true);
    fetchAdmins();
  };

  const handleSelectAdmin = (admin) => {
    // Create a temporary account object to pass to the view
    // We do NOT create a placeholder in the DB anymore
    const tempAccount = {
      bdo_id: admin.id,
      bdo_name: admin.full_name,
      // Include other fields if necessary, but these are the critical ones for AccountProject
    };
    
    setIsModalOpen(false);
    handleTileClick(tempAccount);
    toast.success('Please add a project for this account.');
  };

  const handleTileClick = (account) => {
    setSelectedAccount(account);
    setViewMode('account-project');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedAccount(null);
    fetchProjects(); // Refresh list to get any updates
  };

  const confirmDeleteAccount = (account) => {
    setAccountToDelete(account);
    setIsDeleteDialogOpen(true);
  };

  const executeDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      // Find all projects associated with this account (BDO)
      const projectsToDelete = projectDetails.filter(p => p.bdo_id === accountToDelete.bdo_id);
      
      // Delete all of them
      // We'll use Promise.all to delete them in parallel
      const deletePromises = projectsToDelete.map(project => 
        axiosInstance.delete(`/ccp/project-details/${project.id}`)
      );

      await Promise.all(deletePromises);

      // Update local state
      const remainingProjects = projectDetails.filter(p => p.bdo_id !== accountToDelete.bdo_id);
      setProjectDetails(remainingProjects);
      
      toast.success('Account and all associated projects deleted successfully');
      setIsDeleteDialogOpen(false);
      setAccountToDelete(null);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
  };

  // Filter admins for the modal list
  const filteredAdmins = availableAdmins.filter(admin => 
    admin.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    admin.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const superAdmins = filteredAdmins.filter(admin => admin.is_super_admin);
  const regularAdmins = filteredAdmins.filter(admin => !admin.is_super_admin);

  // Get unique accounts (BDOs)
  const uniqueAccounts = projectDetails.reduce((acc, current) => {
    const x = acc.find(item => item.bdo_id === current.bdo_id);
    if (!x) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, []);

  if (viewMode === 'account-project') {
    return (
      <AccountProject 
        account={selectedAccount}
        axiosInstance={axiosInstance}
        currentUser={currentUser}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Project Details</h2>
          <p className="text-gray-600 dark:text-gray-200">Manage project details and information</p>
        </div>
        {currentUser?.is_super_admin && (
          <Button onClick={handleAddClick} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Account
          </Button>
        )}
      </div>

      {/* Tiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {uniqueAccounts.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">No accounts selected. Click "+ Add Account" to select an admin.</p>
          </div>
        ) : (
          uniqueAccounts.map((account) => {
            // Check access permissions
            const isSuperAdmin = currentUser?.is_super_admin;
            const isOwner = account.bdo_id === currentUser?.id;
            const canAccess = isSuperAdmin || isOwner;

            // Calculate pending approvals for this account
            const isDgmUser = (currentUser?.email?.trim().toLowerCase() === 'dgm@isourceasia.com') || (currentUser?.full_name?.toUpperCase() === 'DONALD G. MADINO');
            const isCadfUser = (currentUser?.email?.trim().toLowerCase() === 'cadf@isourceasiaworkplace.com');
            const isOamUser = (currentUser?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com');
            const isJcpUser = (currentUser?.email?.trim().toLowerCase() === 'jcp@isourceasia.com');
            const isMspUser = (currentUser?.email?.trim().toLowerCase() === 'msp@isourceasia.com.ph');
            
            const pendingCount = projectDetails.filter(p => {
              const isPlaceholder = p.project_type === 'Account_Placeholder';
              if (isPlaceholder || p.bdo_id !== account.bdo_id) return false;

              const isApproved = p.is_approved === true || p.is_approved === 'true' || p.is_approved === 1;
              const isApprovedPricing = p.is_approved_pricing === true || p.is_approved_pricing === 'true' || p.is_approved_pricing === 1;
              const isApprovedCosts = p.is_approved_costs === true || p.is_approved_costs === 'true' || p.is_approved_costs === 1;
              const isApprovedJoygie = p.is_approved_joygie === true || p.is_approved_joygie === 'true' || p.is_approved_joygie === 1;
              const isApprovedOverall = p.is_approved_overall === true || p.is_approved_overall === 'true' || p.is_approved_overall === 1;

              if (isDgmUser) {
                  return !isApproved;
              }
              
              if (isCadfUser) {
                  return isApproved && !isApprovedPricing;
              }

              if (isOamUser) {
                  return isApprovedPricing && !isApprovedCosts;
              }

              if (isJcpUser) {
                  return isApprovedCosts && !isApprovedJoygie;
              }

              if (isMspUser) {
                  return isApprovedJoygie && !isApprovedOverall;
              }

              return false;
            }).length;
            
            const showBadge = pendingCount > 0 && (isDgmUser || isCadfUser || isOamUser || isJcpUser || isMspUser);

            return (
              <Card 
                key={account.id} 
                className={`glass shadow-md transition-all border-l-4 border-l-indigo-500 ${
                  canAccess 
                    ? "hover:shadow-lg cursor-pointer group" 
                    : "opacity-75 cursor-not-allowed grayscale-[0.5]"
                }`}
                onClick={() => {
                  if (canAccess) {
                    handleTileClick(account);
                  }
                }}
              >
                <CardContent className="p-6 flex flex-col items-center justify-center min-h-[120px] relative">
                  {showBadge && (
                    <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-sm z-10">
                      {pendingCount}
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
                    {account.bdo_name}
                  </h3>
                  
                  {/* Remove button at bottom right - only show if user is super admin */}
                  {isSuperAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteAccount(account);
                      }}
                      className="absolute bottom-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-auto px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Selection Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Account</DialogTitle>
            <DialogDescription>
              Choose an administrator account to add to the project details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search admins..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <ScrollArea className="flex-1 pr-4">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Super Admins Section */}
                {superAdmins.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                      <Shield className="h-3 w-3 mr-1" />
                      Super Admins
                    </h4>
                    <div className="space-y-2">
                      {superAdmins.map((admin) => (
                        <div 
                          key={admin.id}
                          onClick={() => handleSelectAdmin(admin)}
                          className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors"
                        >
                          <Avatar className="h-8 w-8 mr-3">
                            <AvatarImage src={admin.avatar_url} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-700">
                              {admin.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{admin.full_name}</p>
                            <p className="text-xs text-gray-500">{admin.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Regular Admins Section */}
                {regularAdmins.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      Regular Admins
                    </h4>
                    <div className="space-y-2">
                      {regularAdmins.map((admin) => (
                        <div 
                          key={admin.id}
                          onClick={() => handleSelectAdmin(admin)}
                          className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors"
                        >
                          <Avatar className="h-8 w-8 mr-3">
                            <AvatarImage src={admin.avatar_url} />
                            <AvatarFallback className="bg-gray-100 text-gray-700">
                              {admin.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{admin.full_name}</p>
                            <p className="text-xs text-gray-500">{admin.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {availableAdmins.length === 0 && !isLoading && (
                  <p className="text-center text-gray-500 py-4">No admins found.</p>
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the account for <strong>{accountToDelete?.bdo_name}</strong>? This will permanently delete all associated projects.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={executeDeleteAccount}>Delete Account</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetails;
